import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { injectIntl } from "react-intl";
import withStyles from "components/with-styles";
import styles from "./styles/form-confirm.css.js";
import IconButton from "components/icon-button";
import Icon from "components/icon";
import SnackbarContent from "@material-ui/core/SnackbarContent";

const getIconName = ({ success, error, warning }) => {
  if (error) {
    return "error";
  }
  if (warning) {
    return "warning";
  }
  return "check";
};

const FormConfirm = ({
  intl,
  classes,
  show = false,
  onClose,
  success = false,
  error = false,
  warning = false,
  label,
  className,
  hideCloseButton = true,
  autoHideDuration = null,
}) => {
  const [open, setOpen] = useState(show);
  const handleClose = onClose || (() => setOpen(false));

  const action = !hideCloseButton
    ? [
        <IconButton key="close" color="inherit" onClick={handleClose}>
          <Icon name="close" className={classes.icon} />
        </IconButton>,
      ]
    : [];

  useEffect(
    () => {
      if (show !== open) {
        setOpen(show);
      }
    },
    [open, show],
  );

  useEffect(
    () => {
      if (open && Boolean(autoHideDuration)) {
        const timeout = setTimeout(onClose, autoHideDuration);
        return () => {
          if (timeout) {
            clearTimeout(timeout);
          }
        };
      }
    },
    [autoHideDuration, onClose, open],
  );

  return label && open ? (
    <SnackbarContent
      className={cn(
        classes.confirm,
        { [classes.error]: error, [classes.success]: success, [classes.warning]: warning },
        className,
      )}
      classes={{
        root: classes.root,
        action: classes.action,
      }}
      message={
        <span id="client-snackbar" className={classes.message}>
          <Icon name={getIconName({ success, error, warning })} className={cn(classes.icon, classes.iconVariant)} />
          {label}
        </span>
      }
      action={action}
    />
  ) : null;
};

FormConfirm.propTypes = {
  /**
   * Duración del mensaje de confirmación en pantalla
   *    - Por defecto no se oculta (autoHideDuration = null)
   */
  autoHideDuration: PropTypes.number,
  /**
   * Clase adicional del componente
   */
  className: PropTypes.string,
  /**
   * Indica si se muestra el botón de cerrar
   */
  hideCloseButton: PropTypes.bool,
  /**
   * Indica si se muestra el confirm
   */
  show: PropTypes.bool,
  /**
   * Indica si se muestra el mensaje y los estilos de éxito
   */
  success: PropTypes.bool,
  /**
   * Indica si se muestra el mensaje y los estilos de error
   */
  error: PropTypes.bool,
  /**
   * Indica si se muestra el mensaje y los estilos de aviso
   */
  warning: PropTypes.bool,
  /**
   * Callback que se invoca cuando se cierra el confirm
   */
  onClose: PropTypes.func,
  /**
   * Texto del mensaje de error/éxito
   */
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

export default withStyles(styles)(injectIntl(FormConfirm));
